import { useEffect, useState } from 'react';
import { resolveTimeBounds } from '../../components/dialogs/timeRangeDomain';
import type {
  CustomDateRange,
  TimeRange,
} from '../../components/dialogs/TimeRangeSelector';
import type { Machine } from '../../gql/generated';

/**
 * Invented machine figures for the Sorting and Health tabs. EDMS exposes no
 * acceptance, ejector, size, valve or runtime data yet.
 *
 * Every figure is a function of the machine and the time, so the same window
 * always shows the same numbers, and a running machine's numbers move as time
 * passes. Long windows average many moments and so move slowly, as real
 * totals do.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** How often a running machine's figures update. */
export const SIMULATION_TICK_MS = 3_000;

/** A sorting shift runs from 05:00 to 23:00, local time. */
const SHIFT_START_HOUR = 5;
const SHIFT_END_HOUR = 23;

/** Share of past days on which a line sorted. */
const SORTING_DAY_SHARE = 0.4;

/** How far back the calendar marks sorting days. */
const MARKED_DAYS_BACK = 365;

/** A window is summed over this many time slices. */
const SLICES_PER_WINDOW = 288;

/** A line sorts 25-40 tonnes an hour. TOMRA states up to 50 t/h for the 5A. */
const MIN_TONNES_PER_HOUR = 25;
const TONNES_PER_HOUR_SPREAD = 15;

export interface OutcomeShare {
  label: string;
  percent: number;
}

export interface EjectorShare extends OutcomeShare {
  /** Why the ejector fired, as shares of all products. */
  reasons: OutcomeShare[];
}

export interface SortingFigures {
  accepted: number;
  ejectors: [EjectorShare, EjectorShare];
  /** Share of products per size band, summing to 100. */
  sizeBands: number[];
  /** Tonnes sorted in the window. */
  tonnes: number;
  /** Share of ejections the ejectors missed, in percent. */
  missedEjections: number;
}

/**
 * Why each ejector fires, with its typical share of all product. Stephen
 * Davitt (TOMRA) gave these figures for a 4A on potatoes.
 */
const EJECTOR_REASONS: { label: string; reasons: OutcomeShare[] }[] = [
  {
    label: 'Ejector A',
    reasons: [
      { label: 'Stones and Clods', percent: 15 },
      { label: 'Foreign Material Inorganic', percent: 0.5 },
      { label: 'Foreign Material Organic', percent: 2 },
    ],
  },
  {
    label: 'Ejector B',
    reasons: [
      { label: 'Green', percent: 5 },
      { label: 'Rot', percent: 1 },
      { label: 'Cuts', percent: 2 },
    ],
  },
];

/**
 * Relative weight of each 25 mm size band, from 0-25 mm up to 175-200 mm. A
 * 4A measures up to 200 mm; potatoes centre near 80 mm and few pass 150 mm.
 */
const SIZE_BAND_WEIGHTS = [0.5, 6, 26, 35, 21, 9, 2, 0.5];

/** A 2400 mm wide 4A has 96 valves. */
export const VALVE_COUNT = 96;

/**
 * Cycles per minute for each valve, before the machine's own spread. Product
 * spreads over the belt, so the outer valves fire least.
 */
const VALVE_CYCLE_PROFILE = Array.from({ length: VALVE_COUNT }, (_, i) => {
  const fromCentre = (i + 0.5) / VALVE_COUNT - 0.5;
  return 2 + 13 * Math.exp(-((fromCentre / 0.28) ** 2));
});

/** How long a valve's rate takes to drift from one value to the next. */
const VALVE_DRIFT_MS = 9_000;

/** Neighbouring valves see the same product, so their rates stay close. */
function smoothAcrossValves(values: number[]): number[] {
  return values.map((value, i) => {
    const before = values[i - 1] ?? value;
    const after = values[i + 1] ?? value;
    return (before + 2 * value + after) / 4;
  });
}

/** Ejector A's share of a valve's cycles: stones and clods outnumber defects. */
const EJECTOR_A_CYCLE_SHARE = 0.65;

/** FNV-1a, so every machine id gets its own seed. */
function hashString(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193);
  }
  return hash >>> 0;
}

/** A repeatable number in [0, 1) for a seed and an integer. */
function noise(seed: number, n: number): number {
  let x = (seed ^ Math.imul(n | 0, 0x9e3779b1)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

/** Noise that drifts smoothly from one step to the next. */
function smoothNoise(seed: number, time: number, stepMs: number): number {
  const position = time / stepMs;
  const step = Math.floor(position);
  const t = position - step;
  const eased = t * t * (3 - 2 * t);
  return noise(seed, step) * (1 - eased) + noise(seed, step + 1) * eased;
}

/** Spreads `noise` from [0, 1) to [1 - spread, 1 + spread). */
function around(value: number, spread: number): number {
  return 1 - spread + 2 * spread * value;
}

function startOfDay(time: number): Date {
  const day = new Date(time);
  day.setHours(0, 0, 0, 0);
  return day;
}

function dayKey(day: Date): number {
  return day.getFullYear() * 400 + day.getMonth() * 32 + day.getDate();
}

export function isMachineRunning(machine: Machine): boolean {
  return machine.state?.state?.startsWith('running') ?? false;
}

/** Repeatable figures for one machine, driven by the time. */
class MachineSimulation {
  private readonly seed: number;

  constructor(
    machineId: string,
    private readonly running: boolean
  ) {
    this.seed = hashString(machineId);
  }

  private salt(n: number): number {
    return (this.seed + Math.imul(n, 0x27d4eb2d)) >>> 0;
  }

  /** A running machine sorts today; past days follow the seed. */
  private sortsOn(day: Date, today: Date): boolean {
    if (day.getTime() === today.getTime()) return this.running;
    return noise(this.salt(1), dayKey(day)) < SORTING_DAY_SHARE;
  }

  /** Whether the machine sorted at `time`. */
  private sortingAt(time: number, now: number): boolean {
    if (time > now) return false;
    const day = startOfDay(time);
    const today = startOfDay(now);
    if (!this.sortsOn(day, today)) return false;
    const hour = new Date(time).getHours();
    // Today's run started at the shift start and is still going.
    if (day.getTime() === today.getTime()) return hour >= SHIFT_START_HOUR;
    return hour >= SHIFT_START_HOUR && hour < SHIFT_END_HOUR;
  }

  sortingDays(now: number): Date[] {
    const today = startOfDay(now);
    const days: Date[] = [];
    for (let back = 0; back <= MARKED_DAYS_BACK; back++) {
      const day = new Date(today);
      day.setDate(day.getDate() - back);
      if (this.sortsOn(day, today)) days.push(day);
    }
    return days;
  }

  private tonnesPerHour(time: number): number {
    return (
      (MIN_TONNES_PER_HOUR + TONNES_PER_HOUR_SPREAD * noise(this.salt(50), 0)) *
      around(smoothNoise(this.salt(51), time, HOUR_MS / 2), 0.1)
    );
  }

  /** Ejector shares and size bands at one moment. */
  /** Missed ejections stay rare: 0.02-0.08% of ejections. */
  private missedEjectionsAt(time: number): number {
    return (
      (0.02 + 0.06 * noise(this.salt(60), 0)) *
      around(smoothNoise(this.salt(61), time, HOUR_MS), 0.5)
    );
  }

  private figuresAt(
    time: number
  ): Omit<SortingFigures, 'tonnes' | 'missedEjections'> {
    const minute = Math.floor(time / MINUTE_MS);
    const ejectors = EJECTOR_REASONS.map(
      ({ label, reasons }, e): EjectorShare => {
        const level =
          around(noise(this.salt(2 + e), 0), 0.25) *
          around(smoothNoise(this.salt(4 + e), time, HOUR_MS), 0.25) *
          around(noise(this.salt(6 + e), minute), 0.1);
        const shares = reasons.map((reason, r) => ({
          label: reason.label,
          percent:
            reason.percent *
            level *
            around(
              smoothNoise(this.salt(10 + 3 * e + r), time, 3 * HOUR_MS),
              0.3
            ),
        }));
        return {
          label,
          percent: shares.reduce((sum, share) => sum + share.percent, 0),
          reasons: shares,
        };
      }
    ) as [EjectorShare, EjectorShare];

    const bandWeights = SIZE_BAND_WEIGHTS.map(
      (weight, b) =>
        weight *
        around(noise(this.salt(20 + b), 0), 0.2) *
        around(smoothNoise(this.salt(30 + b), time, 2 * HOUR_MS), 0.15)
    );
    const bandTotal = bandWeights.reduce((sum, w) => sum + w, 0);

    return {
      accepted: 100 - ejectors[0].percent - ejectors[1].percent,
      ejectors,
      sizeBands: bandWeights.map((w) => (w / bandTotal) * 100),
    };
  }

  /**
   * Figures summed over a window, weighted by sorting time. Null when the
   * machine did not sort in the window.
   */
  sortingFigures(
    range: TimeRange,
    customRange: CustomDateRange | undefined,
    now: number
  ): SortingFigures | null {
    const bounds = resolveTimeBounds(range, customRange, now);
    const to = Math.min(bounds.to, now);
    if (to <= bounds.from) return null;

    // Slices align to whole multiples of their length, so a sliding window
    // keeps the figures of the slices it still covers.
    const sliceMs =
      Math.max(
        1,
        Math.ceil((to - bounds.from) / SLICES_PER_WINDOW / MINUTE_MS)
      ) * MINUTE_MS;
    let weightSum = 0;
    let tonnes = 0;
    let missed = 0;
    const sum = {
      ejectors: EJECTOR_REASONS.map(({ reasons }) => ({
        percent: 0,
        reasons: reasons.map(() => 0),
      })),
      sizeBands: SIZE_BAND_WEIGHTS.map(() => 0),
    };

    for (
      let slice = Math.floor(bounds.from / sliceMs);
      slice * sliceMs < to;
      slice++
    ) {
      const start = Math.max(slice * sliceMs, bounds.from);
      const end = Math.min((slice + 1) * sliceMs, to);
      const middle = (start + end) / 2;
      if (!this.sortingAt(middle, now)) continue;

      const weight = end - start;
      const at = this.figuresAt(middle);
      weightSum += weight;
      tonnes += (this.tonnesPerHour(middle) * weight) / HOUR_MS;
      missed += this.missedEjectionsAt(middle) * weight;
      at.ejectors.forEach((ejector, e) => {
        sum.ejectors[e].percent += weight * ejector.percent;
        ejector.reasons.forEach((reason, r) => {
          sum.ejectors[e].reasons[r] += weight * reason.percent;
        });
      });
      at.sizeBands.forEach((share, b) => {
        sum.sizeBands[b] += weight * share;
      });
    }

    if (weightSum === 0) return null;

    const ejectors = sum.ejectors.map(
      (ejector, e): EjectorShare => ({
        label: EJECTOR_REASONS[e].label,
        percent: ejector.percent / weightSum,
        reasons: ejector.reasons.map((share, r) => ({
          label: EJECTOR_REASONS[e].reasons[r].label,
          percent: share / weightSum,
        })),
      })
    ) as [EjectorShare, EjectorShare];

    return {
      accepted: 100 - ejectors[0].percent - ejectors[1].percent,
      ejectors,
      sizeBands: sum.sizeBands.map((share) => share / weightSum),
      tonnes,
      missedEjections: missed / weightSum,
    };
  }

  /** Today's sorting time, in milliseconds. */
  runtimeToday(now: number): number {
    if (!this.running) return 0;
    const shiftStart = startOfDay(now);
    shiftStart.setHours(SHIFT_START_HOUR);
    return Math.max(0, now - shiftStart.getTime());
  }

  /**
   * Cycles per minute for each valve, in total and per ejector. A running
   * machine's values change on every tick; a stopped one keeps its last.
   */
  healthAt(now: number): {
    valveCycles: number[];
    ejectorCycles: { a: number[]; b: number[] };
  } {
    // A stopped machine keeps one moment's values.
    const time = this.running ? now : 0;
    const level = around(noise(this.salt(40), 0), 0.15);
    // Each valve drifts slowly; the whole belt swells and eases together.
    const belt = around(smoothNoise(this.salt(41), time, VALVE_DRIFT_MS), 0.15);
    const drift = (salt: number, i: number, spread: number) =>
      around(smoothNoise(this.salt(salt + i), time, VALVE_DRIFT_MS), spread);

    const valveCycles = smoothAcrossValves(
      VALVE_CYCLE_PROFILE.map(
        (cycles, i) => cycles * level * belt * drift(100, i, 0.25)
      )
    );
    const a = smoothAcrossValves(
      valveCycles.map(
        (cycles, i) => cycles * EJECTOR_A_CYCLE_SHARE * drift(200, i, 0.2)
      )
    );
    return {
      valveCycles,
      ejectorCycles: { a, b: valveCycles.map((cycles, i) => cycles - a[i]) },
    };
  }
}

export function machineSimulation(machine: Machine): MachineSimulation {
  return new MachineSimulation(machine.id, isMachineRunning(machine));
}

/** The current time, updated every `intervalMs` while `live` is true. */
export function useNow(
  live: boolean,
  intervalMs: number = SIMULATION_TICK_MS
): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [live, intervalMs]);

  return now;
}
